import { useIsDesktop, useIsMobile } from 'hooks/use-size-class';
import styles from './style.module.scss';
import { useState, useEffect, useRef } from 'react';
import Video from 'components/video';

type CommercialHeroProps = {
  maxScale?: number;
  startingY?: number;
  onMinScale: () => void;
  onEnter: () => void;
  onLeave: () => void;
  videoSrc: string;
};

export default function CommercialHero({
  maxScale = 80,
  startingY = 0,
  onMinScale,
  onEnter,
  onLeave,
  videoSrc,
}: CommercialHeroProps) {
  const [scale, setScale] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const hasEntered = useRef(false);
  const heroRef = useRef<HTMLDivElement | null>(null);

  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const scrollSensitivity = isDesktop ? 22 : isMobile ? 25 : 8;
  const STICKY_WRAPPER_HEIGHT = 200; // in vh
  const MAX_SCROLL = STICKY_WRAPPER_HEIGHT * 200;

  const maskSize = 95 + (scale - 1) * 200;

  useEffect(() => {
    if (scale === 1) {
      hasEntered.current = false;
      onMinScale();
    } else if (!isVisible && hasEntered.current) {
      hasEntered.current = false;
      onLeave();
    } else if (isVisible && !hasEntered.current) {
      hasEntered.current = true;
      onEnter();
    }
  }, [scale, onMinScale, maxScale, isVisible, onEnter, onLeave]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = Math.max(window.scrollY - startingY, 0);
      const progress = Math.min((scrollY / MAX_SCROLL) * scrollSensitivity, 1);
      const newScale = 1 + (maxScale - 1) * progress;
      setScale(newScale);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollSensitivity, MAX_SCROLL, maxScale, startingY]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    });

    observer.observe(el);
    return () => {
      observer.unobserve(el);
    };
  }, []);


  return (
    <div style={{ height: `${STICKY_WRAPPER_HEIGHT}vh` }}>
      <div className={styles.component} ref={heroRef}>
        <Video playsInline loop muted poster={'/next/assets/images/exp-commercial/commercial-hero-poster.webp'} src={videoSrc} type="video/mp4" style={{ maskSize: `${maskSize}vw` }} />
      </div>
    </div>
  );
}